import settings from '@/WiseSettings.js';
import axios from 'axios';
import { Preferences } from '@capacitor/preferences';
import fitnessDefaultImage from '../../../assets/fitness_default_image.jpg'
import missingimage from '../../../assets/missingimage.png'
// Used for to populate common state on initial load and reset common state on logout
const getCommonDefaultState = () => {
	return {
		userLocale: null, // populated from main.js on app init
		settings: settings,
		screenMode: null,
		selectedHost: null,
		overrideHost: null, // wisegolf feature, separate idea from having multiple proper hosts
		loadingArticles: false,
		listArticles: [],
		sliderArticles: [],
		loadMoreArticlesPage: 2,
		articlesLoadedFromHost: null,
		articlesLoadedLocale: null,
		articlesLoadedTimestamp: null,
		consentCheckboxText: null,
		contracts: [],
		invoices: [],
		loadingInvoices: false,
		loadingContracts: false,
		commonShowAlert: null, // Alert instance to use onDidDismiss etc... when used this.ShowAlert()
		defaultFitnessImage: fitnessDefaultImage, // Default image for front page enrolled events and possible future workout feature
		errorImage: missingimage, // Articles image when error loading image
		// Benefits
		// FIXME Add default benefits image
		defaultBenefitsImage: missingimage, // If no image on benefits use this
		benefits: [],
		benefitsLoadedLocale: null,
		benefitsLoadedTimestamp: null,
	}
}

export default {
	namespaced: true,
	state: getCommonDefaultState(),
	getters: {
		userLocale: (state) => state.userLocale,
		darkMode: (state) => state.screenMode === 'dark',
		allHosts: (state) => state.settings.hosts,
		selectedHost: (state) => state.selectedHost,
		originalHost: (state) => {
			if (state.selectedHost) {
				const selectedHost = state.settings.hosts.find(host => host.name === state.selectedHost);
				
				if (!selectedHost) {
					console.log('selected host not found', state.selectedHost)
				}

				return selectedHost || state.settings.hosts[0]
			} else {
				// If no host selected return first as default
				return state.settings.hosts[0];
			}
		},
		activeHost: (state, getters) => {
			const { originalHost } = getters;
			
			if (state.overrideHost !== null) {
				return {
					isOverrideHost: true,
					...originalHost,
					...state.overrideHost,
				}
			}

			return originalHost;
		},
		// for shorthand usage
		restUrl: (state, getters) => {
			const { activeHost } = getters;

			return activeHost.restUrl;
		},
		ajaxUrl: (state, getters) => {
			const { activeHost } = getters;

			return activeHost.ajaxUrl;
		},
		baseUrl: (state, getters) => {
			const { activeHost } = getters;

			return activeHost.baseUrl;
		},
		ecomUrl: (state, getters) => {
			const { activeHost } = getters;

			return activeHost.ecomUrl;
		},
		adminUrl: (state, getters) => {
			const { activeHost } = getters;

			return activeHost.adminUrl;
		},
		appauth: (state, getters) => {
			const { activeHost } = getters;

			return activeHost.appauth;
		},
		benefits: (state) => state.benefits,
		// NOTE! This differs from the WiseSettings by having the backend override values!
		appSettings: (state) => state.settings,
		errorImage: (state) => state.errorImage,
		defaultBenefitsImage: (state) => state.defaultBenefitsImage,
	},
	mutations: {
		screenMode(state, value) {
			state.screenMode = value;

			if (value === 'dark') {
				document.body.classList.add('dark')
			} else {
				document.body.classList.remove('dark')
			}

			Preferences.set({ key: `screenMode-${settings.appId}`, value })
		},
		mutateCommonStateToDefault(state) {
			const skipReset = ['screenMode', 'selectedHost', 'userLocale']
			const defaultValues = getCommonDefaultState()

			Object.keys(defaultValues).forEach(key => {
				if (skipReset.includes(key)) return;

				state[key] = defaultValues[key]
			})
		},
		mutateCommonShowAlert(state, commonShowAlert) {
			state.commonShowAlert = commonShowAlert;
		},
		mutateListArticles(state, listArticles) {
			state.listArticles = listArticles;
		},
		mutateMoreListArticles(state, listArticles) {
			state.listArticles = state.listArticles.concat(listArticles);
		},
		mutateLoadingState(state, loadingArticles) {
			state.loadingArticles = loadingArticles
		},
		mutateSliderArticles(state, sliderArticles) {
			state.sliderArticles = sliderArticles;
		},

		mutateSelectedHost(state, selectedHost) {
			state.selectedHost = selectedHost;
			console.log(`%cSelected host is: ${selectedHost}`, 'color: orange; padding: 5px 0')
		},
		mutateOverrideHost(state, overrideHost) {
			state.overrideHost = overrideHost;
			console.log(`%cSelected override host is: ${overrideHost?.name}`, 'color: lightcoral; padding: 5px 0')
		},
		mutateLoadMoreArticlesPage(state, loadMoreArticlesPage) {
			state.loadMoreArticlesPage = loadMoreArticlesPage;
		},
		mutateArticlesLoadedFromHost(state, articlesLoadedFromHost) {
			state.articlesLoadedFromHost = articlesLoadedFromHost;
		},
		mutateArticlesLoadedLocale(state, articlesLoadedLocale) {
			state.articlesLoadedLocale = articlesLoadedLocale;
		},
		mutateArticlesLoadedTimestamp(state, articlesLoadedTimestamp) {
			state.articlesLoadedTimestamp = articlesLoadedTimestamp;
		},
		mutateConsentCheckboxText(state, consentCheckboxText) {
			state.consentCheckboxText = consentCheckboxText;
		},
		mutateContracts(state, contracts) {
			state.contracts = contracts.rows
		},
		mutateloadingContracts(state, loadingContracts) {
			state.loadingContracts = loadingContracts
		},
		mutateUserLocale(state, userLocale) {
			state.userLocale = userLocale;
		},
		mutateBenefits(state, benefits) {
			state.benefits = benefits;
		},
		mutateInvoices(state, invoices) {
			state.invoices = invoices;
		},
		mutateLoadingInvoices(state, loadingInvoices) {
			state.loadingInvoices = loadingInvoices
		},
		mutateBenefitsLoadedLocale(state, benefitsLoadedLocale) {
			state.benefitsLoadedLocale = benefitsLoadedLocale;
		},
		mutateBenefitsLoadedTimestamp(state, benefitsLoadedTimestamp) {
			state.benefitsLoadedTimestamp = benefitsLoadedTimestamp;
		},
		// Used for overwrite settings from DB
		mutateSettings(state, settings) {
			state.settings = settings;
		},
	},
	actions: {
		// Get all settings for current appId from MobileAppSettings. Overwrites defaults in src/WiseSettings.js
		async getMobileAppSettings({ getters, commit, state }) {
			try {
				const { ajaxUrl, appauth } = getters.activeHost
				const { appId } = state.settings
				const { data } = await axios.get(
					`${ ajaxUrl }?getmobileappsettings=1&appid=${ appId }&appauth=${ appauth }`
				)
				let overWriteSettings = data

				// Convert string true/false to boolean and check if string is number (if true and convert to number)
				for (let [key, value] of Object.entries(overWriteSettings)) {
					if(value === "true" || value === true) {
						value = true;
					} else if (value === "false" || value === false) {
						value = false;
					} else if (!isNaN(value)) {
						value = parseInt(value)
					}
					
					overWriteSettings[key] = value
				}

				// Merge settings
				// QUESTION: Is Deep merge settings needed?
				const mergedSettings = {...state.settings, ...overWriteSettings }
				commit('mutateSettings', mergedSettings)
			} catch (e) {
				console.log('getMobileAppSettings failed', e)
			}
		},

		// Get articles category (app / slider_app)
		async getArticles({ getters, commit, state }) {
			try {
				commit('mutateLoadingState', true)
				const sliderArticles = await getSliderArticles(getters, state.userLocale)
				const listArticles = await getListArticles(getters, state.userLocale)
				commit('mutateSliderArticles', sliderArticles.data.items)
				commit('mutateListArticles', listArticles.data.items)
				commit('mutateLoadingState', false)
			} catch (error) {
				commit('mutateLoadingState', false)
				console.error("STORE: getArticles() FAILED")
				console.error(JSON.stringify(error))
				return Promise.reject(error)
			}
		},
		// Load more category app articles (@params object can have page and per_page)
		async loadMoreArticles({ getters, commit, state }, params) {
			try {
				const moreListArticles = await getListArticles(getters, state.userLocale, params.page)
				commit('mutateMoreListArticles', moreListArticles.data.items)
				return moreListArticles.data.items.length
			} catch (error) {
				return Promise.reject(error)
			}
		},

		async getConsentText({ getters, commit, state }) {
			try {
				const { data } = await axios.get(`${getters.activeHost.ajaxUrl}?getconsentcheckbox=1&lang=${state.userLocale}&nocache=${Date.now()}&appauth=${getters.activeHost.appauth}`)
				commit('mutateConsentCheckboxText', data)
			} catch (error) {
				return Promise.reject(error)
			}
		},

		async getUserBenefits({ getters, commit }) {
			try {
				const { userLocale } = getters
				const response = await axios({
					method: 'GET',
					url: `${ getters.activeHost.restUrl }/benefits/user/?lang=${ userLocale }`,
					withCredentials: true,
				})
				const data = response.data;
		
				if (data.rows && data.rows.length) {
					data.rows.forEach((benefit) => {
						benefit.imageUrl = benefit.imageId
							? `${getters.activeHost.baseUrl}?action=getContent&type=img&id=${benefit.imageId}`
							: null;	
					});
				}

				commit('mutateBenefits', data.rows)
			} catch (error) {
				console.error("Failed to fetch user benefits:", error)
				commit('mutateBenefits', [])
			}
		},

		async getBenefits({ getters, commit, state }) {
			/**
			 * This function has a bit problematic relationship to getUserBenefits
			 * - it overrides the benefits loaded (mutateBenefits)
			 * - categories are not being used anymore in "modern times"
			 */
			try {
				// Get benefits categories
				const benefitsCategoriesData = await axios({
					method: 'GET',
					url: `${ getters.activeHost.restUrl }/categories/?producttype=7&categorytype=4&lang=${ state.userLocale }`,
					withCredentials: true,
				})
				const benefitsCategories = benefitsCategoriesData.data.rows

				// Get benfits by categoryId
				for (const [index, category] of benefitsCategories.entries()) {					
					const categoryBenefitsData = await axios({
						method: 'GET',
						url: `${ getters.activeHost.restUrl }/products/?categories.categoryid=${ category.categoryId }&select=productid,meta,type,tickettype,price,benefitprice,trackinventory,maximum,visibility,active,manufacturer,dateofpublication,expirationdate,images&type=7&lang=${ state.userLocale }`,
						withCredentials: true,
					})
					benefitsCategories[index].benefits = categoryBenefitsData.data.rows
				}

				if (benefitsCategories.length > 0) {
					commit('mutateBenefits', benefitsCategories)
					commit('mutateBenefitsLoadedTimestamp', Math.floor(Date.now() / 1000))
					commit('mutateBenefitsLoadedLocale', state.userLocale)
				}
			} catch (error) {
				console.error("getBenefits()", error)
				commit('mutateBenefitsLoadedTimestamp', null)
				commit('mutateBenefitsLoadedLocale', null)
				return Promise.reject(error)
			}
		},

		async getContracts({getters, commit}) {
			commit('mutateloadingContracts', true)
			try {
				const { data } = await axios({
					method: 'GET',
					url: `${ getters.activeHost.restUrl }/contracts/`,
					withCredentials: true,
				})
				//console.log(data.rows);

				if (data.rows) {
					const currentDate = new Date();

					data.rows.forEach( row => {
						if (row.subscriptionEndDate) {
							const endDate = new Date(row.subscriptionEndDate);
							const hasEnded = endDate < currentDate;
							const isEnding = row.subscriptionEndDate && !hasEnded ? true : false;
							row.hasEnded = hasEnded;
							row.isEnding = isEnding
							//console.log(hasEnded); // true if contract has ended, false otherwise
						}

						if (row.subscriptionStartDate) {
							const startDate = new Date(row.subscriptionStartDate);
							const hasStarted = startDate < currentDate;
							row.hasStarted = hasStarted;
						}

						if (row.subscriptionEndDateFixedPeriod && row.subscriptionType === 3) {
							const fixedTermEndsDate = new Date(row.subscriptionEndDateFixedPeriod);
							const fixedTermHasEnded = fixedTermEndsDate < currentDate;
							row.fixedTermHasEnded = fixedTermHasEnded;
						}

						if (row.totalDiscounts) {
							row.totalPrice = row.totalPrice - row.totalDiscounts;
						}
					})

					commit('mutateContracts', data)
				}
				commit('mutateloadingContracts', false)
			} catch (error) {
				console.error("getContracts() error: ", error)
				commit('mutateloadingContracts', false)
			}
		},

		async getBenefitAvailability({ getters, state }, benefitId) {
			try {
				const { data } = await axios({
					method: 'GET',
					url: `${ getters.activeHost.restUrl }/benefit/${ benefitId }/availability?lang=${ state.userLocale }`,
					withCredentials: true,
				})
				return data.rows
			} catch (error) {
				console.error("getBenefitAvailability()", error)
				return Promise.reject(error)
			}
		},

		async postUseBenefit({ getters }, benefitId) {
			try {	
				console.log('postUseBenefit withCredentials true', Date.now())			
				const { data } = await axios({
					method: 'POST',
					url: `${ getters.activeHost.restUrl }/benefit/${ benefitId }/use`,
					data: {},
					withCredentials: true,
				})
				return data
			} catch (error) {
				console.error("useBenefit()", error)
				return Promise.reject(error)
			}
		},

		async getInvoices({ getters, commit }) {
			try {
				commit("mutateLoadingInvoices", true)
				
				let { data } = await axios({
					method: 'GET',
					url: `${ getters.activeHost.restUrl }/invoice/`,
					withCredentials: true,
				})

				const dueInvoiceStatuses = [10, 13, 15, 20, 25]
				const openInvoiceStatuses = [5, 6, 7]
				const invoices = {
					paidInvoices: [],
					unpaidInvoices: [],
					dueInvoices: []
				};

				if (!data) {
					throw new Error(data.errorMessage ? data.errorMessage : "No data received from server");
				}

				// Check unpaid invoices from all invoices
				data.forEach(invoice => {
					if (dueInvoiceStatuses.includes(invoice.status)) {
						invoices.dueInvoices.push(invoice)
					} else if (openInvoiceStatuses.includes(invoice.status)) {
						invoices.unpaidInvoices.push(invoice)
					} else if (invoice.status === 30 || invoice.status === 50) {
						invoices.paidInvoices.push(invoice)	
					}
				})

				invoices.unpaidInvoices = invoices.unpaidInvoices.sort((a, b) => new Date(a.invoiceDueDate) - new Date(b.invoiceDueDate)) // Sort unpaid invoices by due date ascending
				invoices.paidInvoices = invoices.paidInvoices.slice(0, 12) // Limit paid invoices to 12

				commit("mutateInvoices", invoices)
			} catch (error) {
				console.error("getInvoices():", error)
			} finally {
				commit("mutateLoadingInvoices", false)
			}
		}
	}
}

const getListArticles = async (getters, locale = 'fi-fi', page = 1, perPage = 10) => {
	try {
		const { activeHost } = getters
		const { articleCategory } = activeHost
		const category = articleCategory || 'app'
		return await axios.get(`${getters.activeHost.ajaxUrl}?controller=ajax&appmode=1&method=getLastItems2&lang=${locale}&categoryName=${ category }&page=${page}&per_page=${perPage}&nocache=${Date.now()}&appauth=${getters.activeHost.appauth}`)
	} catch (error) {
		console.error(error)
	}
}
const getSliderArticles = async (getters, locale = 'fi-fi', page = 1, perPage = 10) => {
	try {
		return await axios.get(`${getters.activeHost.ajaxUrl}?controller=ajax&appmode=1&method=getLastItems2&lang=${locale}&categoryName=app_slider&page=${page}&per_page=${perPage}&nocache=${Date.now()}&appauth=${getters.activeHost.appauth}`)
	} catch (error) {
		console.error(error)
	}
}